import React from 'react';

import { FeatureTabs } from './FeatureTabs/FeatureTabs';
import { FeaturesCta } from './FeaturesCta/FeaturesCta';
import { PanelItemsOverview } from './PanelItemsOverview/PanelItemsOverview';
import { SolutionList } from './SolutionList/SolutionList';
import { HalfNoiseBackground } from '@components/HalfNoiseBackground/HalfNoiseBackground';
import { Image } from '@components/Image/Image';
import { ImageTooltip } from '@components/ImageTooltip/ImageTooltip';
import { Col } from '@components/Layout/Col/Col';
import { Head } from '@components/Layout/Head';
import { Row } from '@components/Layout/Row/Row';
import { Section } from '@components/Section/Section';
import { Text } from '@components/Text/Text';

import styles from './Features.module.scss';
import dottedLineImage from './dottedLine.svg';
import groupImage from './groupSolutions.jpg';

export const Features: React.FC = () => (
  <>
    <Head
      description="Explore CDN77’s platform features, including website and application performance, security, and client-tailored solutions."
      title="CDN features"
    />
    <header>
      <Section className={styles.headerText} padding={{ top: 150, bottom: 0 }} tag="div">
        <Text
          align="center"
          className="mb-5"
          color="gray900"
          display="block"
          size={56}
          tag="h1"
          weight="bold"
        >
          Explore Core CDN features
        </Text>
        <Text
          align="center"
          color="gray500"
          display="block"
          maxWidth={580}
          size={18}
          tag="p"
          isCentered
        >
          CDN77 provides a range of features to improve performance, security, and user experience
        </Text>
      </Section>
      <FeatureTabs withClientPanelButton />
    </header>

    <main>
      <Section id="client-panel" padding={{ top: 120, bottom: 170 }} theme="dark">
        <div className={styles.clientWrapper}>
          <Text align="center" className="mb-5" color="white" display="block" size={44} tag="h2">
            Client tailored solutions
          </Text>
          <Text
            align="center"
            className={styles.subtitle}
            color="white"
            display="block"
            maxWidth={890}
            opacity={80}
            size={18}
            tag="p"
            isCentered
          >
            The CDN77 Engineering team is always ready to prepare highly specific functionality or
            put a little twist in general setup to bring performance improvements or smoother
            experience.
          </Text>
        </div>
        <Image alt="" className={styles.dottedLine} height={63} src={dottedLineImage} width={7} />
        <Row gapX="xs" gapY="xl" verticalAlign="center">
          <Col md={6}>
            <SolutionList />
          </Col>
          <Col md={6}>
            <ImageTooltip
              placement="bottom-end"
              position={{ top: 63, left: 63 }}
              text="Video engineers Tom and Dan are setting up a custom solution for a client"
            >
              <Image alt="" height={465} src={groupImage} theme="rounded" width={575} />
            </ImageTooltip>
          </Col>
        </Row>
      </Section>
      <Section padding={{ top: 120, bottom: 150 }} theme="tiles">
        <div className={styles.panelHeaderWrapper}>
          <Text
            align="center"
            className="mb-5"
            color="gray900"
            display="block"
            size={44}
            tag="h2"
            weight="bold"
          >
            Client panel
          </Text>
          <Text
            align="center"
            className={styles.subtitle}
            display="block"
            maxWidth={720}
            opacity={80}
            size={18}
            tag="p"
            isCentered
          >
            Intuitive user interface, robust reporting and analytics mixed with full CDN management
            to help you tune your CDN's performance.
          </Text>
        </div>
        <PanelItemsOverview />
      </Section>
      <HalfNoiseBackground height="quarter">
        <FeaturesCta />
      </HalfNoiseBackground>
    </main>
  </>
);
